import { useEffect, useRef } from 'react';

export default function GridOverlay() {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5 });
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let time = 0;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const render = () => {
      const w = canvas.width;
      const h = canvas.height;
      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;

      ctx.clearRect(0, 0, w, h);

      const vpx = w * (0.5 + (mx - 0.5) * 0.15);
      const vpy = h * (0.45 + (my - 0.5) * 0.1);

      time += 0.005;

      // Perspective horizontal lines
      const hLines = 24;
      for (let i = 1; i <= hLines; i++) {
        const t = i / hLines;
        const y = vpy + (h * 1.2 - vpy) * Math.pow(t, 2);
        const alpha = t * 0.08 * (0.8 + Math.sin(time + t * 6) * 0.2);

        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.strokeStyle = `rgba(0, 240, 255, ${alpha})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }

      // Radial lines from vanishing point
      const rLines = 20;
      for (let i = 0; i < rLines; i++) {
        const spread = (i / (rLines - 1)) * 2 - 1;
        const endX = vpx + spread * w * 1.2;
        const endY = h + 50;
        const alpha = (1 - Math.abs(spread) * 0.5) * 0.05;

        ctx.beginPath();
        ctx.moveTo(vpx, vpy);
        ctx.lineTo(endX, endY);
        ctx.strokeStyle = `rgba(0, 240, 255, ${alpha})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }

      // Mouse proximity glow on grid
      const glowX = mx * w;
      const glowY = my * h;
      const gradient = ctx.createRadialGradient(glowX, glowY, 0, glowX, glowY, 150);
      gradient.addColorStop(0, 'rgba(0, 240, 255, 0.04)');
      gradient.addColorStop(1, 'rgba(0, 240, 255, 0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(glowX - 150, glowY - 150, 300, 300);

      // Vanishing point glow
      const vpGlow = ctx.createRadialGradient(vpx, vpy, 0, vpx, vpy, 80);
      vpGlow.addColorStop(0, 'rgba(0, 240, 255, 0.03)');
      vpGlow.addColorStop(1, 'rgba(0, 240, 255, 0)');
      ctx.fillStyle = vpGlow;
      ctx.fillRect(vpx - 80, vpy - 80, 160, 160);

      rafRef.current = requestAnimationFrame(render);
    };
    render();

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      mouseRef.current = {
        x: e.clientX / window.innerWidth,
        y: e.clientY / window.innerHeight,
      };
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="grid-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1,
        pointerEvents: 'none',
        opacity: 0.5,
        mixBlendMode: 'screen',
      }}
    />
  );
}
